/**
 * User Settings Store - Persistent user preferences with Zustand
 *
 * Persists to localStorage with hydration support.
 * Follows cross-platform continuity patterns (desktop/mobile sync-ready).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type Language = 'en' | 'es' | 'de' | 'fr' | 'ja' | 'zh';
export type Density = 'comfortable' | 'compact';
export type FontSize = 'normal' | 'large' | 'xl';
export type ContrastMode = 'normal' | 'high' | 'highest';
export type ColorTheme = 'midnight' | 'galaxy' | 'nebula' | 'aurora' | 'ocean';

// Color theme definitions - sophisticated navy/universe inspired
export const COLOR_THEMES: Record<ColorTheme, {
  label: string;
  bg: string;
  bgSecondary: string;
  bgTertiary: string;
  accent: string;
  accentMuted: string;
  highlight: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderSubtle: string;
  success: string;
  warning: string;
  error: string;
  description: string;
}> = {
  midnight: {
    label: 'Midnight',
    bg: '#0a0a0f',
    bgSecondary: '#0f0f16',
    bgTertiary: '#16161f',
    accent: '#c9a227',
    accentMuted: 'rgba(201, 162, 39, 0.15)',
    highlight: '#f4d03f',
    textPrimary: '#f0f0f5',
    textSecondary: '#a0a0b0',
    textMuted: '#606070',
    border: 'rgba(255, 255, 255, 0.08)',
    borderSubtle: 'rgba(255, 255, 255, 0.04)',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    description: 'Deep black with gold accents',
  },
  galaxy: {
    label: 'Galaxy',
    bg: '#080c14',
    bgSecondary: '#0d1421',
    bgTertiary: '#131c2e',
    accent: '#4a9eff',
    accentMuted: 'rgba(74, 158, 255, 0.12)',
    highlight: '#7bb8ff',
    textPrimary: '#e8edf5',
    textSecondary: '#8b9ab8',
    textMuted: '#4a5568',
    border: 'rgba(74, 158, 255, 0.15)',
    borderSubtle: 'rgba(74, 158, 255, 0.06)',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#f87171',
    description: 'Deep navy with starlight blue',
  },
  nebula: {
    label: 'Nebula',
    bg: '#0c0a14',
    bgSecondary: '#130f1f',
    bgTertiary: '#1a152a',
    accent: '#a78bfa',
    accentMuted: 'rgba(167, 139, 250, 0.12)',
    highlight: '#c4b5fd',
    textPrimary: '#f0eef5',
    textSecondary: '#a8a0c0',
    textMuted: '#5a5070',
    border: 'rgba(167, 139, 250, 0.15)',
    borderSubtle: 'rgba(167, 139, 250, 0.06)',
    success: '#34d399',
    warning: '#fbbf24',
    error: '#fb7185',
    description: 'Deep purple cosmic haze',
  },
  aurora: {
    label: 'Aurora',
    bg: '#06101a',
    bgSecondary: '#0a1825',
    bgTertiary: '#0f2030',
    accent: '#06b6d4',
    accentMuted: 'rgba(6, 182, 212, 0.12)',
    highlight: '#22d3ee',
    textPrimary: '#e8f4f8',
    textSecondary: '#7eb8c8',
    textMuted: '#3a6070',
    border: 'rgba(6, 182, 212, 0.15)',
    borderSubtle: 'rgba(6, 182, 212, 0.06)',
    success: '#10b981',
    warning: '#fbbf24',
    error: '#f87171',
    description: 'Northern lights cyan glow',
  },
  ocean: {
    label: 'Ocean',
    bg: '#060e1a',
    bgSecondary: '#0a1628',
    bgTertiary: '#0f1e36',
    accent: '#3b82f6',
    accentMuted: 'rgba(59, 130, 246, 0.12)',
    highlight: '#60a5fa',
    textPrimary: '#e8eef5',
    textSecondary: '#7a9ac0',
    textMuted: '#3a5070',
    border: 'rgba(59, 130, 246, 0.15)',
    borderSubtle: 'rgba(59, 130, 246, 0.06)',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    description: 'Deep ocean navy blue',
  },
};

export interface UserSettings {
  // Appearance
  theme: Theme;
  colorTheme: ColorTheme;
  density: Density;
  reducedMotion: boolean;

  // Accessibility
  showIconLabels: boolean;
  highContrast: ContrastMode;
  fontSize: FontSize;

  // Localization
  language: Language;
  timezone: string;
  dateFormat: 'relative' | 'absolute';

  // Team Portal Preferences
  sidebarCollapsed: boolean;
  showKeyboardShortcuts: boolean;

  // Privacy & Security
  analyticsEnabled: boolean;
  crashReportsEnabled: boolean;

  // Notifications
  notificationsEnabled: boolean;
  emailDigest: 'daily' | 'weekly' | 'never';
}

interface SettingsState extends UserSettings {
  // Actions
  setTheme: (theme: Theme) => void;
  setColorTheme: (colorTheme: ColorTheme) => void;
  setLanguage: (language: Language) => void;
  setDensity: (density: Density) => void;
  toggleSidebar: () => void;
  toggleReducedMotion: () => void;
  toggleAnalytics: () => void;
  toggleNotifications: () => void;
  setEmailDigest: (digest: 'daily' | 'weekly' | 'never') => void;
  // Accessibility actions
  toggleIconLabels: () => void;
  setHighContrast: (mode: ContrastMode) => void;
  setFontSize: (size: FontSize) => void;
  resetToDefaults: () => void;
}

const defaultSettings: UserSettings = {
  // Appearance
  theme: 'system',
  colorTheme: 'galaxy', // Default to galaxy theme (navy/universe inspired)
  density: 'comfortable',
  reducedMotion: false,

  // Accessibility
  showIconLabels: false,
  highContrast: 'normal',
  fontSize: 'normal',

  // Localization
  language: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateFormat: 'relative',

  // Team Portal Preferences
  sidebarCollapsed: false,
  showKeyboardShortcuts: true,

  // Privacy & Security
  analyticsEnabled: true,
  crashReportsEnabled: true,

  // Notifications
  notificationsEnabled: true,
  emailDigest: 'weekly',
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setTheme: (theme) => set({ theme }),
      setColorTheme: (colorTheme) => set({ colorTheme }),
      setLanguage: (language) => set({ language }),
      setDensity: (density) => set({ density }),

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleReducedMotion: () => set((state) => ({ reducedMotion: !state.reducedMotion })),
      toggleAnalytics: () => set((state) => ({ analyticsEnabled: !state.analyticsEnabled })),
      toggleNotifications: () =>
        set((state) => ({ notificationsEnabled: !state.notificationsEnabled })),

      setEmailDigest: (emailDigest) => set({ emailDigest }),

      // Accessibility actions
      toggleIconLabels: () => set((state) => ({ showIconLabels: !state.showIconLabels })),
      setHighContrast: (highContrast) => set({ highContrast }),
      setFontSize: (fontSize) => set({ fontSize }),

      resetToDefaults: () => set(defaultSettings),
    }),
    {
      name: 'lukhas-team-settings',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Partial hydration - only persist user preferences
      partialize: (state) => ({
        theme: state.theme,
        colorTheme: state.colorTheme,
        density: state.density,
        reducedMotion: state.reducedMotion,
        showIconLabels: state.showIconLabels,
        highContrast: state.highContrast,
        fontSize: state.fontSize,
        language: state.language,
        timezone: state.timezone,
        dateFormat: state.dateFormat,
        sidebarCollapsed: state.sidebarCollapsed,
        showKeyboardShortcuts: state.showKeyboardShortcuts,
        analyticsEnabled: state.analyticsEnabled,
        crashReportsEnabled: state.crashReportsEnabled,
        notificationsEnabled: state.notificationsEnabled,
        emailDigest: state.emailDigest,
      }),
    }
  )
);

// Selector hooks for performance
export const useTheme = () => useSettingsStore((state) => state.theme);
export const useLanguage = () => useSettingsStore((state) => state.language);
export const useSidebarCollapsed = () => useSettingsStore((state) => state.sidebarCollapsed);
export const useDensity = () => useSettingsStore((state) => state.density);
